import { useMemo, useRef, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * BingoBoard90 — full 1..90 board for the Projector. 10 × 9 grid.
 *
 * A cell can be in three states:
 *   • DARK       — not yet called, or called but the reveal hasn't landed.
 *   • LANDING    — the moment the magic trail arrives: 700ms pulse + spark.
 *   • LIT        — permanently marked, sacred.
 *
 * All lit cells use the single hotel theme color (var(--ic-primary))
 * for a uniform, premium look — the visual excitement comes from the
 * ball reveal + magic trail, not from color-coded cells.
 */
export default function BingoBoard90({
  called = [],
  manualMarks = [],
  revealedSet, // Set<number> — values that have finished their reveal
  pulseMs = 700, // duration of the landing pulse on a freshly-revealed cell
}) {
  const calledSet = useMemo(() => new Set(called.map((c) => c.value)), [called]);
  const manualSet = useMemo(() => new Set(manualMarks), [manualMarks]);
  const lastCalled = called.length ? called[called.length - 1].value : null;

  // Track cells that JUST became revealed — used for the landing pulse.
  const [justLanded, setJustLanded] = useState(null);
  const prevRevealedRef = useRef(new Set());
  useEffect(() => {
    if (!revealedSet) return;
    const prev = prevRevealedRef.current;
    let newVal = null;
    revealedSet.forEach((v) => {
      if (!prev.has(v)) newVal = v;
    });
    if (newVal !== null && newVal === lastCalled) {
      setJustLanded(newVal);
      const id = window.setTimeout(() => setJustLanded(null), pulseMs);
      prevRevealedRef.current = new Set(revealedSet);
      return () => window.clearTimeout(id);
    }
    prevRevealedRef.current = new Set(revealedSet);
  }, [revealedSet, lastCalled, pulseMs]);

  const pulseSec = pulseMs / 1000;

  return (
    <div
      data-testid="projector-board"
      className="grid gap-2 w-full h-full"
      style={{
        gridTemplateColumns: "repeat(10, minmax(0, 1fr))",
        gridTemplateRows: "repeat(9, minmax(0, 1fr))",
      }}
    >
      {Array.from({ length: 90 }, (_, i) => {
        const v = i + 1;
        const isCalled = calledSet.has(v);
        const isManual = manualSet.has(v);
        const isRevealed = revealedSet
          ? revealedSet.has(v) || isManual
          : isCalled || isManual;
        const isLandingHere = justLanded === v;
        const isLast = v === lastCalled && isRevealed;

        return (
          <motion.div
            key={v}
            data-testid={`projector-cell-${v}`}
            initial={false}
            animate={
              isLandingHere
                ? {
                    scale: [0.95, 1.22, 1.06],
                    transition: { duration: pulseSec, ease: "easeOut" },
                  }
                : { scale: 1 }
            }
            className="ic-font-display rounded-lg flex items-center justify-center font-bold select-none relative"
            style={{
              fontSize: "clamp(1rem, 1.6vw, 2rem)",
              ...(isRevealed
                ? {
                    background:
                      "color-mix(in srgb, var(--ic-primary) 22%, transparent)",
                    border:
                      "1.5px solid color-mix(in srgb, var(--ic-primary) 60%, transparent)",
                    color: "#ffffff",
                    boxShadow: isLast
                      ? "0 0 32px var(--ic-primary-glow), inset 0 0 12px var(--ic-primary-glow)"
                      : "0 0 12px var(--ic-primary-glow)",
                  }
                : {
                    background: "transparent",
                    border: "1px solid var(--ic-border-subtle)",
                    color: "var(--ic-text-muted)",
                  }),
            }}
          >
            {v}
            <AnimatePresence>
              {isLandingHere && (
                <motion.span
                  key="land-spark"
                  aria-hidden
                  className="absolute inset-0 rounded-lg pointer-events-none"
                  initial={{ opacity: 0.95 }}
                  animate={{ opacity: 0 }}
                  transition={{ duration: pulseSec * 0.85, ease: "easeOut" }}
                  style={{
                    background:
                      "radial-gradient(circle at 50% 50%, #ffffff 0%, var(--ic-primary-glow) 45%, transparent 75%)",
                    filter: "blur(4px)",
                  }}
                />
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
}
